import { format } from 'date-fns';
import { useState } from 'react';
import { Dialog } from '@tempmee/react';
import { useHistory, useParams } from 'react-router-dom';
import Alert from 'react-s-alert';
import {
  useDownloadACHAuthorizationRecord,
  useGetACHAuthorizationRecords,
  useRevokeACHAuthorization,
} from '../../../domain/Payments';
import { TabViewContainer } from '../../commonComponents/TabViewContainer';
import { LegacyDialogBody } from '../../commonComponents/LegacyDialogBody';

const formatDate = (dateString) => {
  if (!dateString) return '-';
  return format(new Date(dateString), 'MMM do, yyyy');
};

const alertOptions = {
  position: 'bottom-right',
  effect: 'slide',
  beep: false,
  timeout: 5000,
  offset: 100,
};

const ACHAuthorization = () => {
  const history = useHistory();
  const { profileType, id } = useParams();

  const { data: records, isLoading } = useGetACHAuthorizationRecords(id);

  const [revokeModalOpen, setRevokeModalOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);

  const handleRevokeCancel = () => {
    setRevokeModalOpen(false);
    setSelectedRecord(null);
  };

  const revokeMutation = useRevokeACHAuthorization(id, {
    onSuccess: () => {
      setRevokeModalOpen(false);
      setSelectedRecord(null);
      Alert.success('ACH authorization has been revoked successfully.', alertOptions);
    },
    onError: (error) => {
      const errorMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        'Failed to revoke ACH authorization.';
      Alert.error(errorMessage, alertOptions);
      handleRevokeCancel();
    },
  });

  const downloadMutation = useDownloadACHAuthorizationRecord({
    onError: () => {
      Alert.error('Failed to download ACH authorization record.', alertOptions);
    },
  });

  const handleDownload = (bankId) => {
    downloadMutation.mutate(bankId);
  };

  const handleRevokeClick = (record) => {
    setSelectedRecord(record);
    setRevokeModalOpen(true);
  };

  const handleRevokeConfirm = () => {
    if (!selectedRecord) return;
    revokeMutation.mutate(selectedRecord.bank_id);
  };

  return (
    <>
      <TabViewContainer
        style={{
          maxWidth: 1200,
          width: '90%',
        }}
        title="ACH Authorization"
        variant="card"
        TitleLeading={
          <div style={{ flex: 1, display: 'flex', alignItems: 'center' }}>
            <button
              className="btn btn-secondary"
              type="button"
              onClick={() => history.push(`/user/${profileType}/${id}/payment`)}
            >
              Back
            </button>
          </div>
        }
      >
        <div style={{ width: '100%', padding: 0 }}>
          {isLoading && (
            <h5
              style={{
                fontFamily: 'Nunito',
                fontWeight: 400,
                textAlign: 'center',
                color: '#435963',
                margin: 25,
              }}
            >
              Loading...
            </h5>
          )}
          {!isLoading && records.length === 0 && (
            <h5
              style={{
                fontFamily: 'Nunito',
                fontWeight: 400,
                textAlign: 'center',
                color: '#435963',
                margin: 25,
              }}
            >
              No ACH authorization records found.
            </h5>
          )}
          {!isLoading && records.length > 0 && (
            <table
              style={{
                width: '100%',
                borderCollapse: 'collapse',
                fontFamily: 'Nunito',
              }}
            >
              <thead>
                <tr
                  style={{
                    backgroundColor: '#d4eef7',
                  }}
                >
                  <th style={{ padding: '12px 16px', textAlign: 'center', fontWeight: 600 }}>
                    Bank Name
                  </th>
                  <th style={{ padding: '12px 16px', textAlign: 'center', fontWeight: 600 }}>
                    Account (Last 4)
                  </th>
                  <th style={{ padding: '12px 16px', textAlign: 'center', fontWeight: 600 }}>
                    Date Created
                  </th>
                  <th style={{ padding: '12px 16px', textAlign: 'center', fontWeight: 600 }}>
                    Download
                  </th>
                  <th style={{ padding: '12px 16px', textAlign: 'center', fontWeight: 600 }}>
                    Revoke
                  </th>
                </tr>
              </thead>
              <tbody>
                {records.map((record) => (
                  <tr
                    key={record.bank_id}
                    style={{
                      borderBottom: '1px solid #efefef',
                    }}
                  >
                    <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                      {record.bank_name || '-'}
                    </td>
                    <td style={{ padding: '12px 16px', textAlign: 'center' }}>{record.last4}</td>
                    <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                      {formatDate(record.created_at)}
                    </td>
                    <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                      <button
                        type="button"
                        style={{
                          color: '#17a2b8',
                          textDecoration: 'none',
                          cursor: 'pointer',
                          background: 'none',
                          border: 'none',
                          padding: 0,
                          font: 'inherit',
                        }}
                        onClick={() => handleDownload(record.bank_id)}
                      >
                        ACH Authorization Record
                      </button>
                    </td>
                    <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                      {record.mandate_status === 'active' ? (
                        <button
                          className="btn"
                          type="button"
                          style={{
                            backgroundColor: '#EA1A1A',
                            color: 'white',
                            borderRadius: '20px',
                            padding: '6px 20px',
                            fontWeight: 600,
                          }}
                          onClick={() => handleRevokeClick(record)}
                        >
                          Revoke
                        </button>
                      ) : (
                        <span style={{ color: record.revoked_at ? '#FF0505' : '#6c757d' }}>
                          {record.revoked_at ? `Revoked - ${formatDate(record.revoked_at)}` : '-'}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </TabViewContainer>

      <Dialog
        open={revokeModalOpen}
        onClose={handleRevokeCancel}
        size="small"
      >
        <LegacyDialogBody>
          <h3
            style={{
              fontFamily: 'Nunito',
              fontWeight: 700,
              fontSize: '18px',
              textAlign: 'center',
              marginBottom: '20px',
            }}
          >
            Are you sure you would like to revoke the ACH Authorization for this ACH account?
          </h3>
          <p
            style={{
              fontFamily: 'Nunito',
              fontSize: '14px',
              textAlign: 'center',
              color: '#666',
              marginBottom: '24px',
              lineHeight: '1.5',
            }}
          >
            Only complete this action if the office directly requested that GoTu revoke the ACH
            Authorization for this account. Once removed, the office will NOT be able to complete
            transactions with this payment method.
          </p>
          <div
            style={{
              display: 'flex',
              width: '100%',
              justifyContent: 'space-between',
              gap: '12px',
            }}
          >
            <button
              className="btn btn-info"
              type="button"
              style={{
                backgroundColor: 'gray',
                flex: 1,
                height: '40px',
                borderWidth: 0,
              }}
              onClick={handleRevokeCancel}
              disabled={revokeMutation.isPending}
            >
              Cancel
            </button>
            <button
              className="btn btn-info"
              type="button"
              style={{
                backgroundColor: '#e74c3c',
                flex: 1,
                height: '40px',
                borderWidth: 0,
              }}
              onClick={handleRevokeConfirm}
              disabled={revokeMutation.isPending}
            >
              {revokeMutation.isPending ? 'Revoking...' : 'Revoke Record'}
            </button>
          </div>
        </LegacyDialogBody>
      </Dialog>
    </>
  );
};

export default ACHAuthorization;
